import { createContext, ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import {
  externalLinkOptions,
  sanitizeOptions,
} from '@packages/shared/core/markdown-to-html/constants';
import { chatEventHandlers } from '@packages/shared/core/chat/analytics/events';
import { checkIsClientSide } from '@packages/shared/core/common/utils/checkIsClientSide';
import {
  MetaClientService,
  MetaServerService,
  MetaService,
  SessionStorageClientService,
  UserService,
  UtmTagsClientService,
  UtmTagsServerService,
  UtmTagsService,
} from '@packages/shared/core/user/services';
import { HttpExpertService } from '@packages/shared/core/experts/services';
import { ExpertService } from '@packages/shared/core/experts/interfaces';
import {
  LocationClientService,
  LocationServerService,
  LocationService,
} from '@packages/shared/core/location/services';
import { Config } from '@packages/shared/interfaces';
import {
  CookiesIdFeatureFlagsDecorator,
  FeatureFlags,
  GrowthBookFeatureFlags,
  ServerFeatureFlags,
} from '@packages/shared/core/feature-flags/services';
import {
  featureFlagsConfig,
  SessionRecorderExperimentGroups,
  sessionRecorderExperimentName,
} from '@packages/shared/core/feature-flags/config';
import {
  ThemeClientService,
  ThemeServerService,
  ThemeService,
} from '@packages/shared/core/theme/services';
import {
  AmplitudeClientService,
  AnalyticsClientService,
  AnalyticsServerService,
  AnalyticsService,
  ArrivalClientService,
  ArrivalServerService,
  ArrivalService,
  BackendAnalyticsService,
  ConversionsAPIService,
  EverflowService,
  FacebookService,
  GoogleAnalyticsService,
  GTMClientService,
} from '@packages/shared/core/analytics/services';
import { ChatService } from '@packages/shared/core/chat/services';
import {
  AuthClientService,
  AuthServerService,
  AuthService,
} from '@packages/shared/core/auth/services';
import {
  AppEnvironmentClientService,
  AppEnvironmentServerService,
  AppEnvironmentService,
  CookiesService,
  CrossDomainStorage,
  GeoService,
  HttpClientBuilder,
  PersistentStorageClientService,
  PersistentStorageServerService,
  PersistentStorageService,
  SessionRecorder,
  ErrorMonitoringService,
  SentryMonitoringClientService,
  SentryMonitoringServerService,
} from '@packages/shared/core/common/services';
import { HoroscopeService } from '@packages/shared/core/horoscope/services';
import { BaseEventHandler } from '@packages/shared/core/analytics/events';
import { featureFlagsEventHandlers } from '@packages/shared/core/feature-flags/events';
import { expertCatalogueEventHandlers } from '@packages/shared/experts-catalogue/analytics/events';
import { simpleRegistrationEventHandlers } from '@packages/shared/simple-registration/analytics/events';
import {
  BaseLogsBatchCache,
  LogsBatchCacheWithLocalStorage,
} from '@packages/shared/core/common/logger/services';
import { Logger } from '@packages/shared/core/common/logger/interfaces';
import { authEventHandlers } from '@packages/shared/core/auth/analytics/events';
import { expertsEventHandlers } from '@packages/shared/core/experts/analytics/events';
import { cookieConsentEventHandlers } from '@packages/shared/core/cookie-consent/analytics/events';
import { LoggerBuilder } from '@packages/shared/core/common/logger/services/LoggerBuilder';
import {
  GrafanaLoggerBatchDestination,
  GrafanaLoggerService,
} from '@packages/shared/core/common/grafana-logger/services';
import { BrowserAppResource } from '@packages/shared/core/common/observability/entities';
import { OTelObservabilitySystemBuilder } from '@packages/shared/core/common/observability/services';
import { ObservabilitySystem } from '@packages/shared/core/common/observability/interfaces';
import {
  ChannelAttributionClientService,
  ChannelAttributionServerService,
  ChannelAttributionService,
} from '@packages/shared/core/common/services/channelAttribution';
import { MarkdownToHtmlServiceBuilder } from '@packages/shared/core/markdown-to-html/services';
import { CookieConsentService } from '@packages/shared/core/cookie-consent/interfaces';
import { OneTrustCookieConsentService } from '@packages/shared/core/cookie-consent/services';
import { srCheatingEventHandlers } from '@packages/shared/simple-registration-cheating/analytics/events';
import { ZendeskSupportFormService } from '@packages/shared/core/support/services';
import { SupportFormService } from '@packages/shared/core/support/interfaces';
import {
  ClarityHeatmapRecordingClientService,
  ClarityHeatmapRecordingServerService,
  HeatmapRecordingService,
} from '../services/ClarityHeatmapRecordingService';

export interface Services {
  appEnvironmentService: AppEnvironmentService;
  analyticsService: AnalyticsService;
  arrivalService: ArrivalService;
  expertService: ExpertService;
  authService: AuthService;
  logger: Logger;
  cookiesService: CookiesService;
  crossDomainStorage: CrossDomainStorage;
  cookieConsentService: CookieConsentService;
  geoService: GeoService;
  userService: UserService;
  sessionRecorder: SessionRecorder;
  utmTagsService: UtmTagsService;
  metaService: MetaService;
  locationService: LocationService;
  chatService: ChatService;
  featureFlags: FeatureFlags;
  persistentStorageService: PersistentStorageService;
  themeService: ThemeService;
  horoscopeService: HoroscopeService;
  clarityHeatmapRecordingService: HeatmapRecordingService;
  observabilitySystem: ObservabilitySystem;
  channelAttributionService: ChannelAttributionService;
  errorMonitoringService: ErrorMonitoringService;
  supportFormService: SupportFormService;
}

export const setupSessionRecorder = async ({
  sessionRecorder,
  appEnvironmentService,
  featureFlags,
}: {
  sessionRecorder: SessionRecorder;
  featureFlags: FeatureFlags;
  appEnvironmentService: AppEnvironmentService;
}) => {
  const experimentGroup = featureFlags.getExperimentGroup(sessionRecorderExperimentName);
  const shouldRecord = experimentGroup === SessionRecorderExperimentGroups.RECORD_ON;

  if (!appEnvironmentService.isProductionEnv() || !shouldRecord) return;

  const randomId = uuid();
  sessionRecorder.identifyUser(randomId);
  sessionRecorder.setShouldRecord(true);
};

export const initServices = (config: Config): Services => {
  const services = checkIsClientSide() ? initClientServices(config) : initServerServices(config);

  return { ...services };
};

const initServerServices = (config: Config): Services => {
  const logger = new LoggerBuilder().getResult();

  const cookiesService = new CookiesService();
  const crossDomainStorage = new CrossDomainStorage(config);
  const cookieConsentService = new OneTrustCookieConsentService(cookiesService, logger);
  const clarityHeatmapRecordingService = new ClarityHeatmapRecordingServerService();
  const appEnvironmentService = new AppEnvironmentServerService(config, logger, cookiesService);
  const observabilitySystem = OTelObservabilitySystemBuilder.create(config).getResult();

  const httpClient = new HttpClientBuilder(config.services.baseUrl)
    .addObservability(observabilitySystem)
    .addRequestId()
    .getResult();

  const askGatewayHttpClient = new HttpClientBuilder(config.services.askGatewayUrl)
    .addObservability(observabilitySystem)
    .addRequestId()
    .addPlatform()
    .getResult();

  const featureFlags = new ServerFeatureFlags();

  const expertService = new HttpExpertService(askGatewayHttpClient, featureFlags);
  const userService = new UserService(httpClient, askGatewayHttpClient);
  const utmTagsService = new UtmTagsServerService();
  const metaService = new MetaServerService();
  const chatService = new ChatService(askGatewayHttpClient);
  const locationService = new LocationServerService();

  const geoService = new GeoService(
    process.env.NEXT_PUBLIC_GEOCODE_URL || '',
    process.env.NEXT_PUBLIC_GEOCODE_API_KEY || '',
  );
  const authService = new AuthServerService({
    httpClient,
    cookiesService,
    crossDomainStorage,
    askGatewayHttpClient,
  });
  const analyticsService = new AnalyticsServerService(logger);
  const arrivalService = new ArrivalServerService();

  const sessionRecorder = new SessionRecorder(
    config.smartlook.apiKey,
    utmTagsService,
    featureFlags,
  );

  const persistentStorageService = new PersistentStorageServerService(logger);
  const themeService = new ThemeServerService(persistentStorageService, logger);
  const errorMonitoringService = new SentryMonitoringServerService();

  const horoscopeService = new HoroscopeService(
    askGatewayHttpClient,
    errorMonitoringService,
    config.domainLocale,
  );

  const channelAttributionService = new ChannelAttributionServerService();

  const markdownToHtmlService = new MarkdownToHtmlServiceBuilder()
    .withExternalLinks(externalLinkOptions)
    .withConfiguredSanitizer(sanitizeOptions)
    .getResult();

  const supportFormService = new ZendeskSupportFormService(askGatewayHttpClient);

  return {
    appEnvironmentService,
    logger,
    cookiesService,
    crossDomainStorage,
    analyticsService,
    arrivalService,
    expertService,
    utmTagsService,
    metaService,
    authService,
    geoService,
    userService,
    sessionRecorder,
    chatService,
    locationService,
    featureFlags,
    persistentStorageService,
    themeService,
    horoscopeService,
    cookieConsentService,
    observabilitySystem,
    clarityHeatmapRecordingService,
    channelAttributionService,
    errorMonitoringService,
    supportFormService,
  };
};

const initClientServices = (config: Config): Services => {
  const loggerHttpClient = new HttpClientBuilder(config.grafanaLogger.url).getResult();

  const logger = new LoggerBuilder()
    .addConsoleTransport()
    .addBatchTransport(
      new GrafanaLoggerBatchDestination(new GrafanaLoggerService(loggerHttpClient)),
      new LogsBatchCacheWithLocalStorage(new BaseLogsBatchCache()),
    )
    .addContext({ app: 'quiz' })
    .getResult();

  const cookiesService = new CookiesService({ domain: config.domainRoot });
  const crossDomainStorage = new CrossDomainStorage(config);
  crossDomainStorage.loadSplitIdFromQueryParams();
  const cookieConsentService = new OneTrustCookieConsentService(cookiesService, logger);
  const appEnvironmentService = new AppEnvironmentClientService(
    config,
    crossDomainStorage,
    cookiesService,
  );

  const growthBookFeatureFlags = new GrowthBookFeatureFlags(
    featureFlagsConfig,
    appEnvironmentService,
    config,
    logger,
  );
  growthBookFeatureFlags.loadFeatures();

  const resource = BrowserAppResource.create();

  resource.setAppName(config.projectName).setEnv(config.env);

  const observabilitySystemBuilder = OTelObservabilitySystemBuilder.create(config)
    .addBrowserTraceProvider({ resource })
    .withSplitId(appEnvironmentService)
    .withHttpSpanExporter({
      url: config.observability.tracesUrl,
      headers: {},
    });

  const observabilitySystem = observabilitySystemBuilder.getResult();

  const httpClient = new HttpClientBuilder(config.services.baseUrl)
    .addObservability(observabilitySystem)
    .addRequestId()
    .getResult();

  const askGatewayHttpClient = new HttpClientBuilder(config.services.askGatewayUrl)
    .addObservability(observabilitySystem)
    .addRequestId()
    .addPlatform()
    .getResult();

  const featureFlags = new CookiesIdFeatureFlagsDecorator(
    growthBookFeatureFlags,
    cookiesService,
    config,
    logger,
  );

  const errorMonitoringService = new SentryMonitoringClientService();
  const utmTagsService = UtmTagsClientService.createFromQueryParams(crossDomainStorage, logger);
  const sessionStorage = new SessionStorageClientService(logger);
  const metaService = new MetaClientService(sessionStorage);

  const expertService = new HttpExpertService(askGatewayHttpClient, growthBookFeatureFlags);
  const userService = new UserService(httpClient, askGatewayHttpClient);
  const amplitudeService = new AmplitudeClientService(
    utmTagsService,
    config,
    observabilitySystem,
    crossDomainStorage,
  );
  const gtmService = new GTMClientService();
  const conversionsAPIService = new ConversionsAPIService();
  const facebookService = new FacebookService(logger);
  const googleAnalyticsService = new GoogleAnalyticsService(
    new HttpClientBuilder(config.googleAnalytics.apiUrl)
      .addObservability(observabilitySystem)
      .getResult(),
    logger,
    utmTagsService,
    cookiesService,
    {
      googleAnalyticsId: config.googleAnalyticsId,
      measurementId: config.googleAnalytics.measurementId,
      apiSecret: config.googleAnalytics.apiSecret,
      engagementTime: config.googleAnalytics.engagementTime,
      clientIdKey: config.googleAnalytics.clientIdKey,
      sessionIdKey: config.googleAnalytics.sessionIdKey,
    },
  );
  const backendAnalyticsService = new BackendAnalyticsService(
    askGatewayHttpClient,
    crossDomainStorage,
    featureFlags,
    logger,
  );
  const everflowService = new EverflowService(utmTagsService, cookiesService, observabilitySystem);
  const chatService = new ChatService(askGatewayHttpClient);
  const locationService = new LocationClientService({ httpClient, askGatewayHttpClient, logger });

  const geoService = new GeoService(
    process.env.NEXT_PUBLIC_GEOCODE_URL || '',
    process.env.NEXT_PUBLIC_GEOCODE_API_KEY || '',
  );

  const sessionRecorder = new SessionRecorder(
    config.smartlook.apiKey,
    utmTagsService,
    featureFlags,
  );
  setupSessionRecorder({
    sessionRecorder,
    featureFlags,
    appEnvironmentService,
  });

  const authService = new AuthClientService({
    httpClient,
    cookiesService,
    crossDomainStorage,
    askGatewayHttpClient,
  });

  const handlers: Array<BaseEventHandler> = [
    ...authEventHandlers,
    ...expertsEventHandlers,
    ...expertCatalogueEventHandlers,
    ...featureFlagsEventHandlers,
    ...chatEventHandlers,
    ...simpleRegistrationEventHandlers,
    ...srCheatingEventHandlers,
    ...cookieConsentEventHandlers,
  ].map(
    (Handler) =>
      new Handler({
        amplitudeService,
        googleAnalyticsService,
        gtmService,
        facebookService,
        backendAnalyticsService,
        sessionRecorder,
        logger,
      }),
  );

  const analyticsService = new AnalyticsClientService(
    amplitudeService,
    gtmService,
    conversionsAPIService,
    facebookService,
    googleAnalyticsService,
    backendAnalyticsService,
    everflowService,
    logger,
    sessionRecorder,
    handlers,
  );

  const clarityHeatmapRecordingService = new ClarityHeatmapRecordingClientService(
    logger,
    analyticsService,
    cookieConsentService,
    errorMonitoringService,
  );

  featureFlags.onTrackExperiment((experiment, result) => {
    clarityHeatmapRecordingService.addCustomTag('experiments', `${experiment.key}: ${result.key}`);
    analyticsService.setUserExperimentGroupName(experiment.key, result.key);
  });

  const persistentStorageService = new PersistentStorageClientService(logger);
  const arrivalService = new ArrivalClientService(
    config,
    featureFlags,
    appEnvironmentService,
    utmTagsService,
    askGatewayHttpClient,
    cookiesService,
    crossDomainStorage,
    persistentStorageService,
    logger,
  );
  const themeService = new ThemeClientService(persistentStorageService, logger);
  const horoscopeService = new HoroscopeService(
    askGatewayHttpClient,
    errorMonitoringService,
    config.domainLocale,
  );

  const channelAttributionService = new ChannelAttributionClientService();

  const markdownToHtmlService = new MarkdownToHtmlServiceBuilder()
    .withExternalLinks(externalLinkOptions)
    .withConfiguredSanitizer(sanitizeOptions)
    .getResult();

  const supportFormService = new ZendeskSupportFormService(askGatewayHttpClient);

  return {
    appEnvironmentService,
    logger,
    cookiesService,
    crossDomainStorage,
    analyticsService,
    arrivalService,
    expertService,
    authService,
    sessionRecorder,
    geoService,
    userService,
    utmTagsService,
    metaService,
    chatService,
    locationService,
    featureFlags,
    persistentStorageService,
    themeService,
    horoscopeService,
    cookieConsentService,
    observabilitySystem,
    clarityHeatmapRecordingService,
    channelAttributionService,
    errorMonitoringService,
    supportFormService,
  };
};

export const ServicesContext = createContext<Services>(undefined!);

type ServicesProviderProps = {
  value: Services;
  children: ReactNode;
};

const ServicesProvider = ({ value, children }: ServicesProviderProps) => {
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
};

export default ServicesProvider;
